<template lang="pug">
div.neutral200(:class="isMobileScreen ? 'pt-4 px-4 pb-4' : 'pa-8'" style="min-height: calc(100dvh - 72px);")
  //- Header section
  .d-flex.align-center(:class="isMobileScreen ? 'justify-end' : 'justify-space-between'")
    .yz-heading-5.text-neutral-700(v-if="!isMobileScreen") Storage locations
    yz-dropdown(v-model="selectedWarehouseSlug" :items="warehouses" item-text="name" item-value="slug" placeholder="Select warehouse" :loading="isLoadingWarehouses" size="lg" :style="isMobileScreen ? 'width: 100%;' : 'min-width: 220px;'")

  yz-card.mt-4(outlined)
    .px-4.pt-2
      //- Desktop toolbar
      template(v-if="!isMobileScreen")
        v-row(no-gutters align="center")
          v-col
            yz-tabs(v-model="tabs" type="line")
              yz-tab(:label="$t('all')")
          v-col(align="end")
            .d-flex.align-center.justify-end
              yz-search-btn(v-model="searchText" @search="onSearch")
              div.ml-1
                customize-column-menu(type="hq_storage_location" :showTooltip="false" iconSize="21")
              yz-btn.ml-3(label="Add new" size="lg" @click="onAddNew")

      //- Mobile toolbar
      template(v-else)
        .d-flex.align-center.justify-space-between.mb-2
          yz-search-btn(v-model="searchText" @search="onSearch")
          yz-btn(icon-only icon="$yzi-plus" size="lg" @click="onAddNew")

    yz-divider

    filter-locations-row.mt-4.px-4(ref="filter" :filters.sync="filters")

    //- Mobile card list view
    template(v-if="isMobileScreen")
      .px-4.mt-4
        //- Loading skeleton
        template(v-if="isFetching")
          v-card.rounded-lg.mb-3(v-for="n in 3" :key="'skeleton-' + n" flat outlined)
            .pa-4
              v-skeleton-loader(type="list-item-three-line")

        //- Empty state
        .text-center.py-8(v-else-if="!locations.length")
          .yz-regular-body-sm.neutral500--text No items found

        //- Card list
        template(v-else)
          v-card.rounded-lg.mb-3(v-for="item in locations" :key="item.id" flat outlined @click="onRowClick(item)")
            .pa-4
              //- Header: Name + 3-dot menu
              .d-flex.align-center.justify-space-between
                .yz-medium-body-sm.neutral700--text.text-truncate.flex-grow-1(style="min-width: 0;") {{ item.value }}
                v-menu(offset-y left rounded="lg")
                  template(#activator="{ on }")
                    yz-btn(icon-only icon="$yzi-dots-horizontal" icon-size="20" size="lg" v-on="on" @click.native.stop)
                  v-list.py-0
                    v-list-item(@click="onEditLocation(item)")
                      v-list-item-title.yz-regular-body-sm.text-neutral-700 Edit
                    v-divider
                    v-list-item(@click="onDeleteLocation(item)")
                      v-list-item-title.yz-regular-body-sm.text-danger-500 Delete

              //- Created date
              .d-flex.align-center.mt-3(v-if="item.created")
                .yz-regular-body-xs.neutral500--text.mr-2 Created
                .yz-regular-body-xs.neutral700--text {{ item.created | humanizeDate }} {{ item.created | displayTime2 }}

              //- Inventory count
              .d-flex.align-center.mt-2(v-if="item.products && item.products.length")
                .yz-regular-body-xs.neutral500--text.mr-2 Inventory
                .yz-regular-body-xs.neutral700--text {{ item.products.length }}

    //- Desktop table view
    .table-scroll-wrapper(v-else :class="{ 'has-data': locations.length > 0 }")
      yz-data-table.mt-2(:headers="headers" :items="locations" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" no-outer-border radius="0" body-height="52px" header-height="36px" header-class="yz-bold-body-xs" header-background-color="white" disable-sort @click:row="onRowClick")
        template(v-slot:item.name="{ item }")
          .yz-regular-body-sm.text-neutral-700 {{ item.value }}
        template(v-slot:item.created="{ item }")
          div(v-if="item.created")
            .yz-regular-body-sm.text-neutral-700 {{ item.created | humanizeDate }}
            .yz-regular-body-xs.text-neutral-500 {{ item.created | displayTime2 }}
          .yz-regular-body-sm.text-neutral-700(v-else) -
        template(v-slot:item.inventory="{ item }")
          .yz-regular-body-sm.text-neutral-700(v-if="item.products && item.products.length") {{ item.products.length }}
          .yz-regular-body-sm.text-neutral-700(v-else) -
        template(v-slot:item.actions="{ item, index }")
          div.d-flex.align-center.justify-end(@click.stop)
            yz-menu(offset-y :top="menuPositionTop(index)" left)
              template(#activator="{ on }")
                yz-btn(v-on="on" icon-only icon="mdi-dots-horizontal" size="lg" color="transparent")
              v-list.py-0
                v-list-item(@click="onEditLocation(item)")
                  v-list-item-title.yz-regular-body-sm.text-neutral-700 Edit
                v-divider
                v-list-item(@click="onDeleteLocation(item)")
                  v-list-item-title.yz-regular-body-sm.text-danger-500 Delete

    yz-pagination.mt-4.px-4(v-if="totalCount > pageSize" v-model="currentPage" :page-size.sync="pageSize" :count="totalCount" change-page-size arrow @input="onPageChanged")

  .dialogs
    yz-delete-confirmation-dialog(v-model="openDeleteDialog" item="location" description="This action will be done immediately and can't be undone. Select \"Confirm\" to proceed." :loading="isLoading" @confirmed="onConfirmDelete" @canceled="onCloseDeleteDialog")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import FilterLocationsRow from '@/components/clinic/inventory/FilterLocationsRow.vue'

export default {
  name: 'InventoryStorageLocations',
  components: { CustomizeColumnMenu, FilterLocationsRow },
  data: () => ({
    isFetching: false,
    isLoading: false,
    isLoadingWarehouses: false,
    searchText: '',
    pageSize: 10,
    totalCount: 0,
    currentPage: 1,
    locations: [],
    tabs: 0,
    filters: [],
    warehouses: [],
    selectedWarehouseSlug: null,
    openDeleteDialog: false,
    selectedLocation: null,
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      storageLocationFields: 'hq/getStorageLocationFields'
    }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    hqCompanyId () {
      return this.company?.branch_of?.id || this.company?.id
    },
    selectedWarehouse () {
      return this.warehouses.find(w => w.slug === this.selectedWarehouseSlug)
    },
    headers () {
      const name = { text: 'Name', value: 'name', enable: true }
      const fields = this.storageLocationFields.filter(item => item.enable)
      fields.map(item => {
        item.text = item.field_name
        item.value = item.id
      })
      const actions = { text: '', value: 'actions', enable: true, sortable: false }
      return [name, ...fields, actions]
    },
  },
  watch: {
    filters () {
      this.currentPage = 1
      this.fetchLocations()
    },
    selectedWarehouseSlug () {
      this.currentPage = 1
      this.fetchLocations()
    },
    pageSize () {
      this.currentPage = 1
      this.fetchLocations()
    },
  },
  mounted () {
    this.fetchWarehouses()
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchWarehouses () {
      this.isLoadingWarehouses = true
      try {
        const resp = await this.$axios.get(`/companies/${this.hqCompanyId}/branches`, { params: { os: 'warehouse', page_size: -1 } })
        this.warehouses = resp.data?.results || []
        if (this.warehouses.length > 0 && !this.selectedWarehouseSlug) {
          this.selectedWarehouseSlug = this.warehouses[0].slug
        }
      } catch {
        this.warehouses = []
      } finally {
        this.isLoadingWarehouses = false
      }
    },
    fetchLocations () {
      if (!this.selectedWarehouseSlug) return
      this.isFetching = true
      const params = {
        page_size: this.pageSize,
        page: this.currentPage,
        ordering: 'value'
      }
      if (this.searchText) {
        params.search = this.searchText
      }
      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })
      this.$axios.get(`/clinics/${this.selectedWarehouseSlug}/inventories/locations`, { params })
        .then(resp => {
          this.locations = resp.data.results
          this.totalCount = resp.data.count
        })
        .catch(err => {
          const message = err.response?.data?.detail
          if (message && message.includes('Invalid page')) {
            this.currentPage = 1
            this.fetchLocations()
          }
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    onSearch () {
      this.currentPage = 1
      this.fetchLocations()
    },
    onPageChanged () {
      this.fetchLocations()
    },
    onRowClick (item) {
      this.onEditLocation(item)
    },
    onAddNew () {
      if (!this.selectedWarehouse) return
      this.$router.push({ name: 'HQAddStorageLocation', query: { warehouse: this.selectedWarehouseSlug, warehouseId: this.selectedWarehouse.id } })
    },
    onEditLocation (item) {
      if (!this.selectedWarehouse) return
      this.$router.push({ name: 'HQEditStorageLocation', params: { id: item.id }, query: { warehouse: this.selectedWarehouseSlug, warehouseId: this.selectedWarehouse.id } })
    },
    onDeleteLocation (item) {
      this.selectedLocation = item
      this.openDeleteDialog = true
    },
    onConfirmDelete () {
      this.isLoading = true
      this.$axios.delete(`/clinics/${this.selectedWarehouseSlug}/inventories/locations/${this.selectedLocation.id}`)
        .then(() => {
          this.currentPage = 1
          this.fetchLocations()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
          this.onCloseDeleteDialog()
        })
    },
    onCloseDeleteDialog () {
      this.selectedLocation = null
      this.openDeleteDialog = false
    },
    menuPositionTop (index) {
      let position = index + 1
      if (position > 7) {
        let start = this.locations.length - 3
        if (position > start) {
          return true
        }
      }
      return false
    },
  }
}
</script>

<style lang="scss" scoped>
.has-data ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  cursor: pointer;
  background: var(--yz-neutral-200) !important;
}
.table-scroll-wrapper {
  overflow-x: auto;
}
.table-scroll-wrapper ::v-deep .yz-data-table {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper > table {
  min-width: 1400px;
}
</style>
